<template>
  <div style="position: relative;" class="bg-white pl-4 pt-4 rounded">
    <div class="d-flex justify-content-between" style="margin-bottom: 1.70rem;">
      <div class="d-flex" @click="goBack()" style="cursor: pointer;">
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="38" viewBox="0 0 40 38" fill="none">
          <path
            d="M38.75 19.0003H1.25M1.25 19.0003L18.9583 1.29199M1.25 19.0003L18.9583 36.7087"
            stroke="#FF7C48"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        <div class="d-flex align-items-center" style="margin-left: 1.25rem;">
          <div>
            <h4 style="font-weight: bold; color: #2c3e50;">{{ $t("audit-control.finding.detail") }}</h4>
          </div>
        </div>
      </div>
      <div>
        <div v-if="!isEditMode" class="d-flex align-items-center">
          <b-button v-if="canEdit" variant="primary" @click="enableEditMode"> <i class="bx bx-pencil font-size-16 align-middle"></i> {{ $t("common.edit") }} </b-button>
        </div>
        <div v-else>
          <b-button variant="success" class="mr-2" @click="save"> <i class="bx bx-check font-size-16 align-middle"></i> {{ $t("common.save") }} </b-button>
          <b-button variant="danger" @click="cancelEditMode"> <i class="bx bx-x font-size-16 align-middle"></i> {{ $t("common.cancel") }} </b-button>
        </div>
      </div>
    </div>

    <ComplianceItemSelect id="compliance-item-modal-select" multiple="true" :title="$t('compliance-item.add')" store="libraryComplianceItem" @selected="addComplianceItems" />

    <content-loading v-if="isLoading" />
    <div v-else class="pr-4 pb-4">
      <div class="bg-light p-4 rounded mb-4 border relative" style="background-color: #e2e2e2; position: relative;">
        <!-- Registration Section (Matches Form Layout) -->
        <b-card class="mb-3">
          <div class="text-right mb-3">
            <i
              class="bx bx-block text-danger font-size-24 mr-3 hand"
              v-if="['AWAITING_APPROVAL', 'APPROVED_EVALUATING', 'REJECTED_EVALUATING', 'EDIT_REQUESTED_EVALUATING'].includes(localFinding.status)"
              @click="cancelFinding"
              v-b-tooltip.hover
              :title="$t('audit-finding.action.cancel')"
            ></i>
          </div>
          <div class="row mb-2">
            <!-- Col 1: No & Status -->
            <div class="col-md-6">
              <div class="form-group w-25">
                <label class="font-weight-bold">{{ $t("finding.no") }}:</label>
                <sweet-input :disableValidation="true" v-model="finding.no" disabled></sweet-input>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group text-right">
                <label class="font-weight-bold">{{ $t("finding.status") }}:</label>
                <div>
                  <b-badge :variant="statusVariant" class="p-2 ">{{ finding.status ? $t(`audit-finding.status.${finding.status.toLowerCase()}`) : "" }}</b-badge>
                </div>
              </div>
            </div>
          </div>

          <!-- Name -->
          <div class="form-group">
            <label class="font-weight-bold">{{ $t("audit-finding.name") }}: <span class="text-danger">*</span></label>
            <div v-if="!isEditMode" class=" ">{{ finding.name | translate }}</div>
            <sweet-input :disableValidation="true" v-else v-model="localFinding.name" />
          </div>

          <!-- Description -->
          <div class="form-group">
            <label class="font-weight-bold">{{ $t("audit-finding.description") }}:</label>
            <div v-if="!isEditMode" class=" h-auto">{{ finding.description | translate }}</div>
            <sweet-textarea v-else v-model="localFinding.description" :rows="3" />
          </div>

          <!-- Dates -->
          <div class="row mb-2">
            <div class="col-md-4">
              <div class="form-group">
                <label class="font-weight-bold">{{ $t("audit-finding.register-date") }}: <span class="text-danger">*</span></label>
                <div v-if="!isEditMode" class=" ">{{ finding.registerDate | date }}</div>
                <b-form-datepicker v-else v-model="localFinding.registerDate" class="mb-2" disabled></b-form-datepicker>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label class="font-weight-bold">{{ $t("audit-finding.detection-date") }}: <span class="text-danger">*</span></label>
                <div v-if="!isEditMode" class="">{{ finding.detectionDate | date }}</div>
                <b-form-datepicker v-else v-model="localFinding.detectionDate" class="mb-2"></b-form-datepicker>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label class="font-weight-bold">{{ $t("audit-finding.type.text") }}:</label>
                <div v-if="!isEditMode" class="">
                  <span v-if="localFinding.type">{{ localFinding.type.name | translate }}</span>
                </div>
                <div v-else>
                  <right-panel
                    class="float-right"
                    @hidden="$refs.typeFilter && $refs.typeFilter.reset()"
                    id="sidebar-new-finding-type"
                    :text="$t('audit-finding.type.new')"
                    width="600px"
                    :icon="'mdi mdi-plus'"
                  >
                    <div class="pl-5 pr-5">
                      <FindingTypeForm />
                    </div>
                  </right-panel>
                  <filter-select
                    ref="typeFilter"
                    :selected="localFinding.type"
                    @filter="onTypeSearch"
                    @changed="onTypeSelect"
                    :placeholder="$t('audit-finding.type.text')"
                    optionLabel="name"
                    :multiple="false"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="row mb-2">
            <div class="col-md-4">
              <div class="form-group">
                <label class="font-weight-bold">{{ $t("audit-finding.root-cause.text") }}:</label>
                <div v-if="!isEditMode" class="h-auto">
                  <span v-if="finding.rootCauses">
                    <span v-for="(rc, idx) in finding.rootCauses" :key="idx">{{ rc.name | translate }}<span v-if="idx < finding.rootCauses.length - 1">, </span></span>
                  </span>
                </div>
                <div v-else>
                  <right-panel
                    class="float-right"
                    @hidden="$refs.rootCauseFilter && $refs.rootCauseFilter.reset()"
                    id="sidebar-new-finding-root-cause"
                    :text="$t('audit-finding.root-cause.new')"
                    width="600px"
                    :icon="'mdi mdi-plus'"
                  >
                    <div class="pl-5 pr-5">
                      <FindingRootCauseForm />
                    </div>
                  </right-panel>
                  <filter-select
                    ref="rootCauseFilter"
                    :selected="localFinding.rootCauses"
                    @filter="onRootCauseSearch"
                    @changed="onRootCauseSelect"
                    :placeholder="$t('audit-finding.root-cause.text')"
                    optionLabel="name"
                    :multiple="true"
                  />
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label class="font-weight-bold">{{ $t("audit-finding.category.text") }}:</label>
                <div v-if="!isEditMode" class="">
                  <span v-if="finding.category">{{ finding.category.name | translate }}</span>
                </div>
                <div v-else>
                  <right-panel
                    class="float-right"
                    @hidden="$refs.categoryFilter && $refs.categoryFilter.reset()"
                    id="sidebar-new-finding-category"
                    :text="$t('audit-finding.category.new')"
                    width="600px"
                    :icon="'mdi mdi-plus'"
                  >
                    <div class="pl-5 pr-5">
                      <FindingCategoryForm />
                    </div>
                  </right-panel>
                  <filter-select
                    ref="categoryFilter"
                    :selected="localFinding.category"
                    @filter="onCategorySearch"
                    @changed="onCategorySelect"
                    :placeholder="$t('audit-finding.category.text')"
                    optionLabel="name"
                    :multiple="false"
                  />
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label class="font-weight-bold">{{ $t("audit-finding.admin") }}:</label>
                <div v-if="!isEditMode" class=" h-auto">
                  <span v-if="finding.admin">{{ finding.admin.name }} {{ finding.admin.surname }}</span>
                </div>
                <organization-member-select v-else :selected="localFinding.admin" @onSelect="onAdminSelect" :organization="currentOrganization" />
              </div>
            </div>
          </div>

          <div class="row mb-2">
            <div class="col-md-4">
              <div class="form-group">
                <label class="font-weight-bold">{{ $t("audit-finding.level") }}:</label>
                <div v-if="!isEditMode" class="">
                  <span v-if="finding.owner && finding.owner.organization">{{ $t("audit-control.control-level.corporate") }}</span>
                  <span v-if="finding.owner && finding.owner.department">{{ $t("department.text") }}</span>
                  <span v-if="finding.owner && finding.owner.process">{{ $t("process.text") }}</span>
                </div>
                <select v-else @change="ownerChanged" class="form-control" v-model="selectedOwnerType">
                  <option value="ORGANIZATION">{{ $t("audit-control.control-level.corporate") }}</option>
                  <option value="DEPARTMENT">{{ $t("department.text") }}</option>
                  <option value="PROCESS">{{ $t("process.text") }}</option>
                </select>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label class="font-weight-bold" v-if="selectedOwnerType == 'ORGANIZATION'">{{ $t("organization.text") }}:</label>
                <label class="font-weight-bold" v-if="selectedOwnerType == 'DEPARTMENT'">{{ $t("department.text") }}:</label>
                <label class="font-weight-bold" v-if="selectedOwnerType == 'PROCESS'">{{ $t("process.text") }}:</label>

                <div v-if="!isEditMode" class="">
                  <span v-if="finding.owner && finding.owner.organization"> {{ finding.owner.organization.name }}</span>
                  <span v-if="finding.owner && finding.owner.department"> {{ finding.owner.department.name | translate }}</span>
                  <span v-if="finding.owner && finding.owner.process"> {{ finding.owner.process.name | translate }}</span>
                </div>
                <div v-else>
                  <div v-if="selectedOwnerType == 'ORGANIZATION'">
                    <filter-select
                      disabled
                      v-show="currentOrganization"
                      :selected="{ _id: currentOrganization._id, name: { tr: currentOrganization.name, en: currentOrganization.name } }"
                      :placeholder="$t('organization.text')"
                      :multiple="false"
                      :custom-label="customLabel"
                    />
                  </div>
                  <div v-show="selectedOwnerType == 'DEPARTMENT'">
                    <filter-select
                      ref="departmentFilter"
                      :selected="localFinding.owner ? localFinding.owner.department : null"
                      :placeholder="$t('department.text')"
                      :multiple="false"
                      @filter="onDepartmentSearch"
                      @changed="onDepartmentSelect"
                    />
                  </div>
                  <div v-show="selectedOwnerType == 'PROCESS'">
                    <filter-select
                      ref="processFilter"
                      @filter="onProcessSearch"
                      :selected="localFinding.owner ? localFinding.owner.process : null"
                      :placeholder="$t('process.text')"
                      :multiple="false"
                      @changed="onProcessSelect"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label class="font-weight-bold">{{ $t("audit-finding.class.text") }}:</label>
                <div v-if="!isEditMode" class="">
                  <span v-if="finding.class">{{ $t("audit-finding.class-options." + finding.class.toLowerCase()) }}</span>
                </div>
                <b-form-select v-else v-model="localFinding.class" :options="findingClassOptions">
                  <template #first>
                    <b-form-select-option :value="null">{{ $t("common.select") }}</b-form-select-option>
                  </template>
                </b-form-select>
              </div>
            </div>
          </div>

          <div class="row mb-2">
            <div class="col-md-4">
              <div class="form-group">
                <label class="font-weight-bold">{{ $t("finding.target.criticality") }}: {{ isCriticalityMandatory ? "*" : "" }}</label>
                <div v-if="!isEditMode" class="">
                  <span v-if="localFinding.criticality">{{ $t("audit-control.criticality." + localFinding.criticality.toLowerCase()) }}</span>
                </div>
                <b-form-select v-else v-model="selectedTargetIndex" :options="findingTargetOptions" @change="onCriticalityChange">
                  <template #first>
                    <b-form-select-option :value="null">{{ $t("common.select") }}</b-form-select-option>
                  </template>
                </b-form-select>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <label class="font-weight-bold">{{ $t("audit-finding.suggestions") }}:</label>
              <div v-if="!isEditMode" class="">
                {{ localFinding.suggestions }}
              </div>
              <sweet-textarea v-else v-model="localFinding.suggestions" :rows="3" />
            </div>

            <div class="col-md-6">
              <label class="font-weight-bold">{{ $t("risk.text") }}:</label>
              <div v-if="!isEditMode" class="">
                {{ localFinding.risk }}
              </div>
              <sweet-textarea v-else v-model="localFinding.risk" :rows="3" />
            </div>
          </div>
        </b-card>

        <!-- Accordions -->

        <!-- 1. Related Control (Collapsible) -->
        <b-card no-body class="custom-card shadow-none custom-accordion mb-3">
          <b-card-header header-tag="header" role="tab" class="bg-white shadow-sm border-bottom-0">
            <a v-b-toggle="'accordion-control'" class="text-primary d-block font-weight-bold" href="javascript: void(0);">
              <h5 class="m-0"><i class="bx bx-shield mr-2"></i>{{ $t("audit-control.control.text") }}</h5>
            </a>
          </b-card-header>
          <b-collapse id="accordion-control" role="tabpanel" v-model="accordionState.control">
            <b-card-body>
              <div class="text-right mb-2" v-if="isEditMode || ['APPROVED_EVALUATING', 'EDIT_REQUESTED_EVALUATING'].includes(localFinding.status)">
                <b-button variant="outline-primary" size="sm" @click="$bvModal.show('control-select')"> <i class="bx bx-plus"></i> {{ $t("common.add") }} </b-button>
              </div>

              <table class="table table-bordered table-striped">
                <thead class="bg-soft-info">
                  <tr>
                    <th>{{ $t("audit-control.control.no") }}</th>
                    <th>{{ $t("audit-control.control.name") }}</th>
                    <th>{{ $t("audit-control.control.category") }}</th>
                    <th>{{ $t("audit-control.control.status") }}</th>
                    <th v-if="isEditMode" style="width: 50px;"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="localFinding.control">
                    <td>{{ localFinding.control.no }}</td>
                    <td>{{ localFinding.control.name | translate }}</td>
                    <td>
                      <span v-if="localFinding.control.category">{{ localFinding.control.category.name | translate }}</span>
                    </td>
                    <td>{{ localFinding.control.status ? $t("audit-control.status." + localFinding.control.status.toLowerCase()) : "" }}</td>
                    <td v-if="isEditMode" class="text-center">
                      <i class="bx bx-trash hand text-danger font-size-18" @click="localFinding.control = undefined"></i>
                    </td>
                  </tr>
                  <tr v-if="!localFinding.control">
                    <td :colspan="isEditMode ? 5 : 4" class="text-center">{{ $t("common.no-data") }}</td>
                  </tr>
                </tbody>
              </table>
            </b-card-body>
          </b-collapse>
        </b-card>

        <!-- 1.5 Related Risks (Collapsible) -->
        <b-card no-body class="custom-card shadow-none custom-accordion mb-3">
          <b-card-header header-tag="header" role="tab" class="bg-white shadow-sm border-bottom-0">
            <a v-b-toggle="'accordion-risks'" class="text-info d-block font-weight-bold" href="javascript: void(0);">
              <h5 class="m-0"><i class="bx bx-error text-danger mr-2"></i>{{ $t("finding.related-risks") }}</h5>
            </a>
          </b-card-header>
          <b-collapse id="accordion-risks" role="tabpanel" v-model="accordionState.risks">
            <b-card-body>
              <div
                class="d-flex justify-content-end mb-3"
                v-if="isEditMode || ['APPROVED_EVALUATING', 'EDIT_REQUESTED_EVALUATING', 'AWAITING_APPROVAL', 'DETECTED'].includes(localFinding.status)"
              >
                <!-- <div class="mr-2" style="width: 300px; text-align: left;">
                  <filter-select ref="riskFilter" @filter="onRiskSearch" @changed="onRiskSelect" :placeholder="$t('risk.text') + ' ' + $t('common.select')" :multiple="false" />
                </div> -->
                <b-button variant="outline-info" size="sm" style="height: 38px;" v-b-toggle.sidebar-new-finding-risk> <i class="bx bx-plus"></i> {{ $t("risk.new") }} </b-button>
              </div>

              <table class="table table-bordered table-striped table-light">
                <thead class="bg-soft-info">
                  <tr>
                    <th>{{ $t("risk.no") }}</th>
                    <th>{{ $t("risk.status.text") }}</th>
                    <th>{{ $t("risk.description") }}</th>
                    <th>{{ $t("risk.degree.title") }}</th>
                    <th>{{ $t("risk.applied-risk-value.text") }}</th>
                    <th
                      v-if="isEditMode || ['APPROVED_EVALUATING', 'EDIT_REQUESTED_EVALUATING', 'AWAITING_APPROVAL', 'DETECTED'].includes(localFinding.status)"
                      style="width: 50px;"
                    ></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="risk in localFinding.relatedRisks" :key="risk._id">
                    <td>{{ risk.no }}</td>
                    <td>
                      <span v-if="risk.status">{{ $t("risk.status." + risk.status.toLowerCase()) }}</span>
                    </td>
                    <td>{{ risk.description | translate }}</td>
                    <td>
                      <span v-if="risk.degree">{{ $t("risk.degree." + risk.degree.toLowerCase()) }}</span>
                    </td>
                    <td>
                      <span v-if="risk.appliedRiskValue">{{ $t("risk.applied-risk-value." + risk.appliedRiskValue.toLowerCase()) }}</span>
                    </td>
                    <td
                      v-if="isEditMode || ['APPROVED_EVALUATING', 'EDIT_REQUESTED_EVALUATING', 'AWAITING_APPROVAL', 'DETECTED'].includes(localFinding.status)"
                      class="text-center"
                    >
                      <i class="bx bx-trash hand text-danger font-size-18" @click="unlinkRisk(risk._id)"></i>
                    </td>
                  </tr>
                  <tr v-if="!localFinding.relatedRisks || localFinding.relatedRisks.length === 0">
                    <td :colspan="6" class="text-center">{{ $t("common.no-data") }}</td>
                  </tr>
                </tbody>
              </table>

              <b-sidebar id="sidebar-new-finding-risk" :title="$t('risk.new')" right shadow width="1100px" backdrop>
                <div class="p-3">
                  <organization-risk-form :initial-findings="[localFinding._id]" @save="onRiskCreated" @cancel="$root.$emit('bv::toggle::collapse', 'sidebar-new-finding-risk')" />
                </div>
              </b-sidebar>
            </b-card-body>
          </b-collapse>
        </b-card>

        <!-- 2. Related Compliance Items (Collapsible) -->
        <b-card no-body class="custom-card shadow-none custom-accordion mb-3">
          <b-card-header header-tag="header" role="tab" class="bg-white shadow-sm border-bottom-0">
            <a v-b-toggle="'accordion-compliances'" class="text-primary d-block font-weight-bold" href="javascript: void(0);">
              <h5 class="m-0"><i class="bx bx-check-circle mr-2"></i>{{ $t("policy.related-compliances") }}</h5>
            </a>
          </b-card-header>
          <b-collapse id="accordion-compliances" role="tabpanel" v-model="accordionState.compliances">
            <b-card-body>
              <div class="text-right mb-2" v-if="isEditMode || ['APPROVED_EVALUATING', 'EDIT_REQUESTED_EVALUATING'].includes(localFinding.status)">
                <b-button variant="outline-primary" size="sm" @click="$bvModal.show('compliance-item-modal-select')"> <i class="bx bx-plus"></i> {{ $t("common.add") }} </b-button>
              </div>

              <table class="table table-bordered table-striped table-light">
                <thead class="bg-soft-secondary">
                  <tr>
                    <td class="w-10">{{ $t("compliance-item.compliance") }}</td>
                    <td class="w-10">{{ $t("compliance-item-group.text") }}</td>
                    <td class="w-10">{{ $t("compliance-item.no") }}</td>
                    <td class="w-10">{{ $t("compliance-item.level") }}</td>
                    <td class="w-25">{{ $t("compliance-item.name") }}</td>
                    <td class="w-30">{{ $t("compliance-item.description") }}</td>
                    <td class="w-5" v-if="isEditMode"></td>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in localFinding.complianceItems" :key="item._id">
                    <td>{{ item.compliance }}</td>
                    <td>
                      <span v-if="item.group">{{ item.group.name | translate }}</span>
                    </td>
                    <td>{{ item.no }}</td>
                    <td>{{ item.level }}</td>
                    <td>{{ item.name | translate }}</td>
                    <td>{{ item.description | translate }}</td>
                    <td v-if="isEditMode">
                      <i class="bx bx-trash hand text-danger font-size-18" @click="removeComplianceItem(item._id)"></i>
                    </td>
                  </tr>
                  <tr v-if="localFinding.complianceItems && localFinding.complianceItems.length == 0">
                    <td :colspan="isEditMode ? 7 : 6" class="text-center">{{ $t("common.no-data") }}</td>
                  </tr>
                </tbody>
              </table>
            </b-card-body>
          </b-collapse>
        </b-card>

        <!-- 3. Approval (Collapsible) -->
        <b-card no-body class="custom-card shadow-none custom-accordion mb-3">
          <b-card-header header-tag="header" role="tab" class="bg-white shadow-sm border-bottom-0">
            <a v-b-toggle="'accordion-approval'" class="text-primary d-block font-weight-bold" href="javascript: void(0);">
              <h5 class="m-0"><i class="bx bx-user-check mr-2"></i>{{ $t("audit-control.approval.status.text") }}</h5>
            </a>
          </b-card-header>
          <b-collapse id="accordion-approval" role="tabpanel" v-model="accordionState.approval">
            <b-card-body>
              <!-- Action Box -->
              <!-- Action Box -->
              <div v-if="['EVALUATING', 'EDIT_REQUESTED_EVALUATING', 'REJECTED_EVALUATING'].includes(localFinding.status)" class="mb-4">
                <div class="card border-light bg-light">
                  <div class="card-body">
                    <h5 class="card-title mb-3">{{ $t("audit-control.approval.send") }}</h5>
                    <div class="row">
                      <div class="col-md-12">
                        <b-form-group :label="$t('finding.approval.request-note')" class="font-weight-bold">
                          <b-form-textarea v-model="approvalNote" :placeholder="$t('common.enterNote')" rows="3"></b-form-textarea>
                        </b-form-group>
                      </div>
                      <div class="col-md-12 text-right">
                        <b-button @click="requestApproval()" :disabled="isSendingApproval" :loading="isSendingApproval" variant="info" class="px-4">
                          <i class="bx bx-send font-size-16 align-middle mr-1"></i> {{ $t("audit-control.approval.send") }}
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Box -->
              <div v-if="localFinding.status === 'AWAITING_APPROVAL' && canResolveApproval" class="mb-4">
                <div class="card border-light bg-light">
                  <div class="card-body">
                    <h5 class="card-title mb-3">{{ $t("audit-control.approval.status.text") }}</h5>
                    <div class="row">
                      <div class="col-md-12">
                        <b-form-group :label="$t('audit-control.approval.status.text')" class="font-weight-bold">
                          <b-form-radio-group
                            v-model="approvalAction"
                            :options="[
                              { text: $t('audit-control.approval.status.approved'), value: 'APPROVED' },
                              { text: $t('audit-finding.status.rejected_evaluating') || 'Denetlenen Bulguya Katılmadı', value: 'REJECTED' },
                              { text: $t('audit-finding.action.request-edit'), value: 'REQUEST_EDIT' },
                            ]"
                            buttons
                            button-variant="outline-primary"
                            name="approval-action-radios"
                          ></b-form-radio-group>
                        </b-form-group>
                      </div>
                      <div class="col-md-12">
                        <b-form-group :label="$t('finding.approval.response-note')" class="font-weight-bold">
                          <b-form-textarea v-model="approvalNote" :placeholder="$t('common.enterNote')" rows="3"></b-form-textarea>
                        </b-form-group>
                      </div>
                      <div class="col-md-12 text-right">
                        <b-button @click="resolveApproval()" :disabled="isSendingApproval || !approvalAction" :loading="isSendingApproval" variant="primary" class="px-4">
                          {{ $t("common.send") }}
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Approval History Table -->
              <div v-if="localFinding.approvals && localFinding.approvals.length > 0">
                <h6 class="mb-3 font-weight-bold">{{ $t("audit-control.approval.approval-history") }}</h6>
                <div class="table-responsive">
                  <table class="table table-bordered table-striped table-hover mb-0">
                    <thead class="bg-light">
                      <tr>
                        <th>{{ $t("audit-control.approval.approval.status") }}</th>
                        <th>{{ $t("audit-control.approval.approval.requested-by") }}</th>
                        <th>{{ $t("audit-control.approval.approval.requested-at") }}</th>
                        <th>{{ $t("audit-finding.targetApprovalDate") }}</th>
                        <th>{{ $t("finding.approval.request-note") }}</th>
                        <th>{{ $t("audit-control.approval.approval.responded-by") }}</th>
                        <th>{{ $t("audit-control.approval.approval.responded-at") }}</th>
                        <th>{{ $t("finding.approval.response-note") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(approval, index) in localFinding.approvals" :key="index">
                        <td>
                          <b-badge :variant="getApprovalStatusVariant(approval.status)">
                            {{
                              approval.status === "REJECTED"
                                ? $t("audit-finding.status.rejected_evaluating") || "Denetlenen Bulguya Katılmadı"
                                : approval.status
                                ? $t("audit-control.approval.status." + approval.status.toLowerCase())
                                : ""
                            }}
                          </b-badge>
                        </td>
                        <td>{{ approval.requestedBy.name }} {{ approval.requestedBy.surname }}</td>
                        <td>{{ approval.requestedAt | date }}</td>
                        <td>{{ getTargetApprovalDate(approval) | date }}</td>
                        <td>{{ approval.requestNote | translate }}</td>
                        <td>
                          <span v-if="approval.respondedBy">{{ approval.respondedBy.name }} {{ approval.respondedBy.surname }}</span>
                          <span v-else>-</span>
                        </td>
                        <td>
                          <span v-if="approval.respondedAt">{{ approval.respondedAt | date }}</span>
                          <span v-else>-</span>
                        </td>
                        <td>{{ approval.responseNote | translate }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else class="text-center text-muted p-3">
                {{ $t("common.no-data") }}
              </div>
            </b-card-body>
          </b-collapse>
        </b-card>

        <!-- 4. Action Plan (Collapsible) -->
        <b-card no-body class="custom-card shadow-none custom-accordion mb-3">
          <b-card-header header-tag="header" role="tab" class="bg-white shadow-sm border-bottom-0">
            <a v-b-toggle="'accordion-action-plan'" class="text-primary d-block font-weight-bold" href="javascript: void(0);">
              <h5 class="m-0"><i class="bx bx-task mr-2"></i>{{ $t("audit-finding.action-plan.text") }}</h5>
            </a>
          </b-card-header>
          <b-collapse id="accordion-action-plan" role="tabpanel" v-model="accordionState.actionPlan">
            <b-card-body>
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="font-weight-bold m-0">{{ $t("audit-finding.action-plan.tasks") }}</h6>
                <div>
                  <b-button v-if="canAddTasks" variant="primary" size="sm" @click="openTaskSelectModal" class="mr-2"> <i class="bx bx-plus"></i> {{ $t("common.add") }} </b-button>
                  <b-button v-if="canCompleteFinding" variant="success" size="sm" @click="completeFinding">
                    <i class="bx bx-check"></i> {{ $t("audit-finding.action-plan.complete") || "Bulguyu Kapat" }}
                  </b-button>
                </div>
              </div>

              <div v-if="actionPlanTasks && actionPlanTasks.length > 0">
                <div v-for="task in actionPlanTasks" :key="'task-' + task._id" class="mb-2">
                  <task-item
                    :task="task"
                    :isItDefinition="false"
                    :unlink="true"
                    :readonly="false"
                    @unlinked="removeTaskFromActionPlan(task._id)"
                    @removeClicked="removeTaskFromActionPlan($event._id)"
                    @click="openTaskDetailModal(task)"
                  />
                </div>
              </div>
              <div v-else class="text-muted text-center py-4">
                {{ $t("audit-finding.action-plan.no-tasks") }}
              </div>
            </b-card-body>
          </b-collapse>
        </b-card>

        <!-- 4.5. Files -->
        <EntityFileSection
          v-if="finding._id"
          entityType="finding"
          :entityId="finding._id"
          :files="finding.files || []"
          :canUpload="canEdit"
          uploadBaseUrl="finding"
          @uploaded="fetchDetails()"
        />

        <!-- 5. History (Collapsible) -->
        <b-card no-body class="custom-card shadow-none custom-accordion mb-3">
          <b-card-header header-tag="header" role="tab" class="bg-white shadow-sm border-bottom-0">
            <a v-b-toggle="'accordion-history'" class="text-primary d-block font-weight-bold" href="javascript: void(0);">
              <h5 class="m-0"><i class="bx bx-history mr-2"></i>{{ $t("common.history") }}</h5>
            </a>
          </b-card-header>
          <b-collapse id="accordion-history" role="tabpanel" v-model="accordionState.history">
            <b-card-body>
              <OrganizationFindingHistory :id="finding._id" :history="finding.history" v-if="finding._id" />
            </b-card-body>
          </b-collapse>
        </b-card>
      </div>

      <!-- Action Modal -->
      <b-modal v-model="actionModal.show" :title="actionModal.title" @ok="confirmAction" :ok-title="$t('common.yes')" :cancel-title="$t('common.cancel')" centered>
        <b-form-group :label="$t('common.note')" label-for="action-note">
          <b-form-textarea id="action-note" v-model="actionModal.note" :placeholder="$t('common.enterNote')" rows="3"></b-form-textarea>
        </b-form-group>
      </b-modal>
    </div>

    <TaskDetail @onSave="onTaskSaved" />

    <AuditControlSelectModal
      id="control-select"
      :title="$t('audit-control.control.add')"
      :hide-created-button="true"
      :add-button-text="$t('audit-control.select')"
      :pre-selected-ids="selectedControlIds"
      @selected="addControls"
      :limit="1"
    />
  </div>
</template>

<script>
import SweetInput from "@c/form/sweet-input";
import SweetTextarea from "@c/form/sweet-textarea";
import ComplianceItemSelect from "../audit-control/control/compliance-item-select.vue";
import OrganizationMemberSelect from "@cc/organization-member-select";
import FindingTypeForm from "./library/workspace-library-finding-type-form";
import FindingRootCauseForm from "./library/workspace-library-finding-root-cause-form";
import FindingCategoryForm from "./library/workspace-library-finding-category-form";
import AuditControlSelectModal from "../audit-control/audit/audit-control-select-modal.vue";
import OrganizationFindingHistory from "./organization-finding-history.vue";
import OrganizationRiskForm from "@/modules/risk/organization-risk-form.vue";

import TaskDetail from "@/components/client/task/task-detail.vue";
import EntityFileSection from "@c/entity-file-section.vue";
import rest from "@/rest-client";

export default {
  components: {
    ComplianceItemSelect,
    SweetInput,
    SweetTextarea,
    OrganizationMemberSelect,
    FindingTypeForm,
    FindingRootCauseForm,
    FindingCategoryForm,
    AuditControlSelectModal,
    OrganizationFindingHistory,
    TaskDetail,
    OrganizationRiskForm,
    EntityFileSection,
  },
  data() {
    return {
      isEditMode: false,
      selectedOwnerType: "ORGANIZATION",
      localFinding: {
        complianceItems: [],
        approval: null,
        rootCauses: [],
      },
      selectedTargetIndex: undefined,
      approvalStatus: null,
      approvalAction: null,
      approvalNote: null,
      isSendingApproval: false,
      accordionState: {
        control: false, // Collapsible
        risks: false, // Collapsible
        compliances: false, // Collapsible
        approval: false, // Collapsible
        actionPlan: false, // Collapsible
        history: false, // Collapsible
      },
      actionModal: {
        show: false,
        title: "",
        note: "",
        type: null, // 'CANCEL' or 'PASSIVE'
      },
    };
  },
  computed: {
    targetConfig() {
      return this.$store.getters["organizationFindingTarget/selectedEntity"];
    },
    findingTargets() {
      return this.targetConfig?.criticality || {};
    },
    findingTargetOptions() {
      return ["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((k) => ({
        value: k,
        text: this.$t("audit-control.criticality." + k.toLowerCase()),
      }));
    },
    selectedControlIds() {
      return this.localFinding.control ? [this.localFinding.control._id] : [];
    },

    canEdit() {
      // Allow edit for DETECTED and EDIT_REQUESTED_EVALUATING
      const editableStatuses = ["DETECTED", "EDIT_REQUESTED_EVALUATING"];
      return editableStatuses.includes(this.localFinding.status);
    },
    canResolveApproval() {
      if (this.localFinding.status !== "AWAITING_APPROVAL") return false;

      // Check if current user is the assignee

      const currentApproval = this.localFinding.approvals && this.localFinding.approvals.length > 0 ? this.localFinding.approvals[0] : null;

      if (currentApproval && currentApproval.status === "PENDING" && currentApproval.assignee) {
        return currentApproval?.assignee?._id === this.currentAccount?._id;
      }

      // Fallback to permission check if no assignee set (legacy support)
      return this.currentAccountHasPermissions(["ORGANIZATION_INFORMATION_TECHNOLOGY_AUDITOR"]);
    },
    isLoading() {
      return this.$store.getters["organizationFinding/isLoading"];
    },
    finding() {
      return this.$store.getters["organizationFinding/editedEntity"] || {};
    },
    calculatedDueDate() {
      const key = this.finding?.criticality;
      const targetItem = key && this.findingTargets ? this.findingTargets[key] : null;
      if (targetItem?.resolutionDuration) {
        const date = this.calculateTargetDate(new Date(), targetItem.resolutionDuration);
        return date ? date.getTime() : null;
      }
      return new Date().getTime() + 30 * 24 * 60 * 60 * 1000;
    },
    statusVariant() {
      const map = {
        AWAITING_APPROVAL: "secondary",

        ACTION_PLAN_MONITORING: "info",
        EXTENSION_APPROVAL: "warning",
        COMPLETED: "success",
        PASSIVE: "secondary",
        CANCELED: "danger",
        APPROVED_EVALUATING: "success",
        REJECTED_EVALUATING: "danger",
        EDIT_REQUESTED_EVALUATING: "warning",
      };
      return map[this.finding.status] || "secondary";
    },
    findingClassOptions() {
      return [
        { value: "NOTABLE_CONTROL_DEFICIENCY", text: this.$t("audit-finding.class-options.notable_control_deficiency") },
        { value: "SIGNIFICANT_CONTROL_DEFICIENCY", text: this.$t("audit-finding.class-options.significant_control_deficiency") },
        { value: "IMPROVEMENT_RECOMMENDATION", text: this.$t("audit-finding.class-options.improvement_recommendation") },
      ];
    },
    actionPlanTasks() {
      return this.finding.actionPlan || [];
    },
    isLastApprovalApproved() {
      if (!this.finding.approvals || this.finding.approvals.length === 0) {
        return false;
      }
      // Latest approval is at index 0
      const lastApproval = this.finding.approvals[0];
      return lastApproval.status === "APPROVED";
    },
    canAddTasks() {
      // Must be in APPROVED_EVALUATING or ACTION_PLAN_MONITORING status
      const validStatuses = ["APPROVED_EVALUATING", "ACTION_PLAN_MONITORING"];
      return validStatuses.includes(this.finding.status);
    },
    canCompleteFinding() {
      if (!this.finding || !["APPROVED_EVALUATING", "ACTION_PLAN_MONITORING"].includes(this.finding.status)) return false;
      if (this.finding.admin?._id !== this.currentAccount?._id) return false;
      if (!this.actionPlanTasks || this.actionPlanTasks.length === 0) return false;
      return this.actionPlanTasks.every((task) => task.status === "DONE");
    },
    isCriticalityMandatory() {
      // Criticality is mandatory if status is NOT one of the initial/passive states
      const optionalStatuses = ["AWAITING_APPROVAL", "PENDING", "PASSIVE", "CANCELED"];
      return !optionalStatuses.includes(this.localFinding.status);
    },
  },
  watch: {
    finding: {
      handler(val) {
        if (val) {
          this.syncLocalFinding(val);
        }
      },
      immediate: true,
      deep: true,
    },
  },
  async mounted() {
    this.$store.dispatch("organizationFindingTarget/fetchTarget");
    const findingId = this.$route.params.id;
    if (findingId && findingId !== "undefined") {
      const { data } = await this.$store.dispatch("organizationFinding/findById", findingId);
      this.$store.commit("organizationFinding/editedEntity", data);
    }
  },
  methods: {
    openTaskDetailModal(task) {
      this.$store.commit("task/showTaskDetailModal", true);
      this.$bvModal.hide();
      this.$root.$emit("bv::hide::modal");
      this.$bvModal.hide("policy-detail-modal");

      return this.$store.dispatch("task/fetchDetail", task._id);
    },
    async requestApproval() {
      const confirm = await this.confirm(this.$t("audit-control.approval.send") + "?");
      if (!confirm) return;

      try {
        await this.$store.dispatch("organizationFinding/requestApproval", {
          findingId: this.finding._id,
          reporterNote: this.approvalNote,
        });
        this.$toast.success(this.$t("common.success"));
        await this.fetchDetails();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    onCriticalityChange(key) {
      if (!key) return;

      const target = this.findingTargets[key];

      if (target) {
        // 1. Set Criticality (Snapshot)
        this.localFinding.criticality = key.toUpperCase();

        // 3. Calculate Target Dates
        const approvalDate = this.calculateTargetDate(new Date(), target.approvalDuration);
        const resolutionDate = this.calculateTargetDate(new Date(), target.resolutionDuration);

        this.$set(this.localFinding, "targetApprovalDate", approvalDate);
        this.$set(this.localFinding, "targetResolutionDate", resolutionDate);
      }
    },
    syncLocalFinding(finding) {
      if (!finding || Object.keys(finding).length === 0) return;

      // Removed "detectingPerson" from localizedFields as it might be a string in some contexts
      const localizedFields = ["name", "description", "suggestions", "risk", "auditorRecommendations", "level", "manager", "approver"];

      // Use direct spread to preserve reactivity of properties if possible, or just copy values
      const prepared = this.prepareFormForEdit(finding, localizedFields);

      this.localFinding = {
        ...prepared,
        complianceItems: finding.complianceItems || [],
        rootCauses: finding.rootCauses || [],

        approval: finding.approval || null,
        targetApprovalDate: finding.targetApprovalDate || null,
        targetResolutionDate: finding.targetResolutionDate || null,
      };

      if (finding.approval) {
        this.approvalStatus = finding.approval.status;
      }

      if (finding.criticality) {
        this.selectedTargetIndex = finding.criticality;
      }

      // Set owner type
      if (finding.owner) {
        if (finding.owner.department) this.selectedOwnerType = "DEPARTMENT";
        else if (finding.owner.process) this.selectedOwnerType = "PROCESS";
        else this.selectedOwnerType = "ORGANIZATION";
      } else {
        this.selectedOwnerType = "ORGANIZATION";
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    enableEditMode() {
      this.isEditMode = true;
      // Re-sync to ensure we have fresh data for editing
      this.syncLocalFinding(this.finding);
    },
    cancelEditMode() {
      this.isEditMode = false;
      this.syncLocalFinding(this.finding);
    },
    async fetchDetails() {
      const findingId = this.$route.params.id;
      if (findingId && findingId !== "undefined") {
        const { data } = await this.$store.dispatch("organizationFinding/findById", findingId);
        this.$store.commit("organizationFinding/editedEntity", data);
      }
    },
    async save() {
      try {
        if (!this.localFinding.name || !this.localFinding.description || !this.localFinding.registerDate || !this.localFinding.detectionDate) {
          this.$toast.warning(this.$t("common.validation-error"));
          return;
        }

        if (this.isCriticalityMandatory && (this.selectedTargetIndex === null || this.selectedTargetIndex === undefined)) {
          this.$toast.warning(this.$t("common.validation-error"));
          return;
        }
        await this.$store.dispatch("organizationFinding/update", this.localFinding);
        this.$toast.success(this.$t("common.saved"));
        this.isEditMode = false;
        await this.fetchDetails();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    cancelFinding() {
      this.actionModal = {
        show: true,
        title: this.$t("audit-finding.action.cancel-confirm"),
        note: "",
        type: "CANCEL",
      };
    },
    async confirmAction(bvModalEvt) {
      if (!this.actionModal.note) {
        bvModalEvt.preventDefault();
        this.$toast.error(this.$t("validation.required"));
        return;
      }

      try {
        if (this.actionModal.type === "CANCEL") {
          await this.$store.dispatch("organizationFinding/cancel", {
            findingId: this.finding._id,
            note: this.actionModal.note,
          });
        }
        this.$toast.success(this.$t("common.success"));
        await this.fetchDetails();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    async completeFinding() {
      const confirm = await this.confirm(this.$t("audit-finding.action.complete-confirm"));

      if (confirm) {
        this.localFinding.status = "COMPLETED";
        await this.save();
      }
    },
    addComplianceItems(items) {
      this.localFinding.complianceItems = [...items];
    },
    removeComplianceItem(itemId) {
      this.localFinding.complianceItems = this.localFinding.complianceItems.filter((item) => item._id !== itemId);
    },
    addControls(items) {
      const newItem = items[0];
      this.$set(this.localFinding, "control", newItem);
    },
    onAdminSelect(admin) {
      this.localFinding.admin = admin;
    },
    async onTypeSearch(value, callback) {
      const response = await this.$store.dispatch(`workspaceLibraryFindingType/paginate`, {
        options: { limit: 50, sort: { [`name.${this.currentLanguage}`]: 1 } },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    onTypeSelect(value) {
      this.localFinding.type = value;
    },
    async onRootCauseSearch(value, callback) {
      const response = await this.$store.dispatch(`workspaceLibraryFindingRootCause/paginate`, {
        options: { limit: 50, sort: { [`name.${this.currentLanguage}`]: 1 } },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    onRootCauseSelect(value) {
      this.localFinding.rootCauses = value;
    },
    async onCategorySearch(value, callback) {
      const response = await this.$store.dispatch(`workspaceLibraryFindingCategory/paginate`, {
        options: { limit: 50, sort: { [`name.${this.currentLanguage}`]: 1 } },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    onCategorySelect(value) {
      this.localFinding.category = value;
    },
    ownerChanged(e) {
      if (e.target.value == "ORGANIZATION") {
        this.onOrganizationSelect();
      }
      setTimeout(() => {
        this.$refs?.departmentFilter?.reset();
        this.$refs?.processFilter?.reset();
      }, 500);
    },
    onOrganizationSelect() {
      const owner = { organization: this.currentOrganization };
      this.localFinding = { ...this.localFinding, owner };
    },
    customLabel(value) {
      return this.$options.filters.translate(value.name);
    },
    async onDepartmentSearch(value, callback) {
      const response = await this.$store.dispatch(`organizationDepartment/paginate`, {
        options: { limit: 50, sort: { [`name.${this.currentLanguage}`]: 1 } },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    onDepartmentSelect(department) {
      const owner = { department };
      this.localFinding = { ...this.localFinding, owner };
    },
    async onProcessSearch(value, callback) {
      const response = await this.$store.dispatch(`organizationProcess/paginate`, {
        options: { limit: 50, sort: { [`name.${this.currentLanguage}`]: 1 } },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    onProcessSelect(process) {
      const owner = { process };
      this.localFinding = { ...this.localFinding, owner };
    },
    async resolveApproval() {
      if (!this.approvalAction) {
        this.$toast.error(this.$t("common.validation_error"));
        return;
      }

      try {
        this.isSendingApproval = true;

        let note = this.approvalNote;

        await this.$store.dispatch("organizationFinding/saveApproval", {
          findingId: this.finding._id,
          status: this.approvalAction,
          responseNote: note,
        });
        this.$toast.success(this.$t("common.success"));
        await this.fetchDetails();
        this.approvalNote = null;
        this.approvalAction = null;
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.isSendingApproval = false;
      }
    },
    getApprovalStatusVariant(status) {
      const map = {
        PENDING: "warning",
        APPROVED: "success",
        REJECTED: "danger",
        REQUEST_EDIT: "info",
      };
      return map[status] || "secondary";
    },
    // Action Plan Methods
    startCreatingActionPlan() {
      this.isCreatingActionPlan = true;
      this.isEditingActionPlan = false;
    },
    startEditingActionPlan() {
      this.isEditingActionPlan = true;
      this.isCreatingActionPlan = false;
    },
    cancelActionPlanForm() {
      this.isCreatingActionPlan = false;
      this.isEditingActionPlan = false;
    },
    async saveActionPlan(payload) {
      try {
        const url = this.isEditingActionPlan ? `/finding/${this.finding._id}/action-plan` : `/finding/${this.finding._id}/action-plan`;

        const method = this.isEditingActionPlan ? "put" : "post";

        await rest[method](url, payload);

        this.$toast.success(this.$t("common.success"));
        this.isCreatingActionPlan = false;
        this.isEditingActionPlan = false;
        await this.fetchDetails();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
        if (this.$refs.actionPlanForm) {
          this.$refs.actionPlanForm.isSubmitting = false;
        }
      }
    },
    openTaskSelectModal() {
      if (!this.findingTargets) {
        this.$toast.warning(this.$t("finding.target.missing"));
        return;
      }

      // Open TaskDetail modal for creating a new task
      this.$store.commit("task/showTaskDetailModal", true);
      this.$store.commit("task/isCreating", true);

      const priority = this.finding.criticality === "LOW" ? "1" : this.finding.criticality === "MEDIUM" ? "2" : this.finding.criticality === "HIGH" ? "3" : "4";

      // Initialize form with finding context and target completion date
      this.$store.commit("task/form", {
        reporter: this.finding.admin,
        duedate: this.calculatedDueDate,
        priority: priority,
        causeds: [this.finding._id],
        caused: {
          entityType: "finding",
          entity: {
            finding: {
              _id: this.finding._id,
              no: this.finding.no,
            },
          },
        },
      });
    },
    async onTaskSaved(task) {
      if (task) {
        // Add the newly created task to the action plan
        await this.addTasksToActionPlan(task);
      }
    },

    async addTasksToActionPlan(task) {
      try {
        //eğer aksiyon planında yoksa eklemeli
        if (this.finding.actionPlan.find((t) => t.taskId === task._id)) {
          return this.fetchDetails();
        }
        await rest.post(`/finding/${this.finding._id}/action-plan/tasks`, {
          taskId: task._id,
        });
        // this.$toast.success(this.$t("common.success"));
        await this.fetchDetails();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async removeTaskFromActionPlan(taskId) {
      try {
        await rest.delete(`/finding/${this.finding._id}/action-plan/tasks/${taskId}`);
        this.$toast.success(this.$t("common.success"));
        await this.fetchDetails();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async completeFinding() {
      const confirm = await this.confirm(this.$t("audit-finding.action-plan.complete-confirm") || "Bulguyu kapatmak istediğinize emin misiniz?");
      if (!confirm) return;

      try {
        await rest.post(`/finding/${this.finding._id}/complete`);
        this.$toast.success(this.$t("common.success"));
        await this.fetchDetails();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    async unlinkRisk(riskId) {
      const confirm = await this.confirm(this.$t("alert.approve"));
      if (!confirm) return;

      try {
        await rest.delete(`/finding/${this.finding._id}/risks/${riskId}`);
        this.$toast.success(this.$t("common.success"));
        await this.fetchDetails();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    async linkExistingRisk(riskId) {
      if (!riskId) return;
      try {
        await rest.post(`/finding/${this.finding._id}/risks/${riskId}`);
        this.$toast.success(this.$t("common.success"));
        await this.fetchDetails();
        if (this.$refs.riskFilter) {
          this.$refs.riskFilter.reset();
        }
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    async onRiskSelect(risk) {
      if (!risk) return;
      await this.linkExistingRisk(risk._id);
    },

    async onRiskSearch(value, callback) {
      const response = await this.$store.dispatch(`organizationRisk/paginate`, {
        options: { limit: 50 },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },

    async onRiskCreated(risk) {
      if (risk && risk._id) {
        await this.linkExistingRisk(risk._id);
      }
      // this.$root.$emit("bv::toggle::collapse", "sidebar-new-finding-risk");
    },

    calculateTargetDate(startDate, duration) {
      if (!startDate || !duration || duration.value === null || duration.value === undefined || !duration.unit) {
        return null;
      }

      const { value, unit } = duration;
      const numValue = Number(value);
      const date = new Date(startDate);
      const upperUnit = unit.toUpperCase();
      if (upperUnit === "WEEK") date.setDate(date.getDate() + numValue * 7);
      else if (upperUnit === "MONTH") date.setMonth(date.getMonth() + numValue);
      else if (upperUnit === "DAY") date.setDate(date.getDate() + numValue);

      return date;
    },
    getTargetApprovalDate(approval) {
      const key = this.finding?.criticality;
      const targetItem = key && this.findingTargets ? this.findingTargets[key] : null;
      if (!targetItem?.approvalDuration) return null;
      return this.calculateTargetDate(approval.requestedAt, targetItem.approvalDuration);
    },
  },
};
</script>

<style scoped>
.title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}
.r-acc-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
</style>
